import { useState } from 'react'
import { IconArrowRight } from '@tabler/icons-react'
import { ChangeText } from '../components/ChangeText'
import { SectionCard } from '../components/SectionCard'
import { StatusBadge } from '../components/StatusBadge'
import { formatCount, formatDays } from '../format'
import type { CohortTarget, Measure, usePopulationMeasures } from '../hooks'
import { measureUrgency } from '../theme'

const TOP = 4

function FocusItem({ measure, onOpen }: { measure: Measure; onOpen: () => void }) {
  const urgency = measureUrgency(measure.highPatients, measure.openPatients)
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`${measure.label} patients`}
      className={`group flex h-full w-full cursor-pointer flex-col rounded-lg border border-l-4 border-slate-200 bg-white p-4 text-left shadow-xs transition hover:shadow-md ${urgency.accent}`}
    >
      <div className="flex items-start justify-between gap-3">
        <span className="font-semibold text-slate-900">{measure.label}</span>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium tabular-nums ${urgency.pill}`}
        >
          {formatCount(measure.openPatients)} patients
        </span>
      </div>
      <p className="mt-1 line-clamp-2 text-sm text-slate-500">{measure.definition}</p>
      <div className="mt-auto pt-3">
        <dl className="grid grid-cols-2 gap-x-3 gap-y-2 border-t border-slate-100 pt-3 text-xs">
          <div>
            <dt className="text-slate-500">Risk split</dt>
            <dd className="font-semibold text-slate-900 tabular-nums">
              {formatCount(measure.highPatients)} high · {formatCount(measure.mediumPatients)}{' '}
              medium
            </dd>
          </div>
          <div className="text-right">
            <dt className="text-slate-500">This period</dt>
            <dd className="font-semibold text-slate-900 tabular-nums">
              {formatCount(measure.openedInPeriod)} opened
            </dd>
          </div>
          <div>
            <dt className="text-slate-500">Median time to resolve</dt>
            <dd className="font-semibold text-slate-900 tabular-nums">
              {formatDays(measure.closureTime.current.medianDays)}
            </dd>
          </div>
          <div className="flex items-end justify-end">
            <ChangeText value={measure.closureTime.changeDays} unit="days" better="down" />
          </div>
        </dl>
        <div className="mt-3 flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-blue-700">
            View patients
            <IconArrowRight
              size={14}
              className="transition group-hover:translate-x-0.5"
              aria-hidden
            />
          </span>
          <StatusBadge status={measure.status} />
        </div>
      </div>
    </button>
  )
}

/** The top-ranked measures; each opens the drawer on exactly that measure's patients. */
export function FocusNow({
  measures,
  onOpenCohort,
}: {
  measures: ReturnType<typeof usePopulationMeasures>
  onOpenCohort: (target: CohortTarget) => void
}) {
  const [showAll, setShowAll] = useState(false)
  const ranked = [...(measures.data?.measures ?? [])].sort((a, b) => a.rank - b.rank)
  const shown = showAll ? ranked : ranked.slice(0, TOP)

  return (
    <SectionCard
      title="Focus now"
      subtitle={`Top ${Math.min(TOP, ranked.length)} measures across the selected population`}
      definition={measures.data?.definitions.ranking}
      frame="open"
      query={measures}
      isEmpty={ranked.length === 0}
      emptyText="No active measures."
      actions={
        ranked.length > TOP && (
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="inline-flex cursor-pointer items-center gap-1 text-sm font-semibold text-blue-700 hover:underline"
          >
            {showAll ? `Show top ${TOP}` : `View all ${ranked.length} measures`}
            {!showAll && <IconArrowRight size={14} aria-hidden />}
          </button>
        )
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {shown.map((measure) => (
          <FocusItem
            key={measure.slug}
            measure={measure}
            onOpen={() => onOpenCohort({ type: 'measure', slug: measure.slug })}
          />
        ))}
      </div>
    </SectionCard>
  )
}
